import React, { useEffect } from "react";
import {
	Route,
	createBrowserRouter,
	createRoutesFromElements,
	RouterProvider,
	Navigate,
} from "react-router-dom";
import { OAuthPopup } from "@tasoskakour/react-use-oauth2";

import { useAppDispatch, useAppSelector } from "./app/hooks";
import { setAspUrl } from "./app/siteSlice";
import { UserRole } from "./app/userSlice";

import Public from "./templates/Public";
import Private from "./templates/Private";
import ErrorPage from "./templates/ErrorPage";
import RequireRoles from "./templates/RequireSpecificRoles";
import RequireCourse from "./templates/RequireCourse";
import RequireCourseOrAdminSession from "./templates/RequireCourseOrAdmin";
import RequireAdmin from "./templates/RequireAdmin";
import RequireAdminSession from "./templates/RequireAdminSession";
import RouteFromArg from "./templates/RouteFromArg";
import SiteConfig from "./templates/SiteConfig";

import Status from "./features/status/Status";
import Login from "./features/login/Login";
import Logout from "./features/login/Logout";
import Courses from "./features/courses/Courses";
import ManageCourse from "./features/courses/ManageCourse";
import EditCourseByAdmin from "./features/courses/editCourseByAdmin/EditCourseByAdmin";
import Home from "./features/home/Home";
import Writing from "./features/writing/Writing";
import SelectOrganizer from "./features/writing/organizing/SelectOrganizer";
import Organize from "./features/writing/organizing/Organize";
import Write from "./features/writing/drafting/Write";
import Preview from "./features/writing/drafting/Preview";
import ViewDraftAutosaveHistory from "./features/writing/draftAutosaveHistory/ViewDraftAutosaveHistory";
import PeerReviews from "./features/peerReviews/PeerReviews";
import PeerReview from "./features/peerReviews/PeerReview";
import Lessons from "./features/lessons/Lessons";
import Lesson from "./features/lessons/Lesson";
import Reports from "./features/reports/Reports";
import StudentReports from "./features/reports/student/StudentReports";
import StudentPerformanceReport from "./features/reports/student/performance/PerformanceReport";
import StudentProgressReport from "./features/reports/student/progress/ProgressReport";
import StudentPromptReport from "./features/reports/student/prompt/PromptReport";
import StudentUsageReport from "./features/reports/student/usage/UsageReport";
import CourseReports from "./features/reports/course/CourseReports";
import CourseUsageReport from "./features/reports/course/usage/UsageReport";
import LessonUsageByCategoryReport from "./features/reports/course/usage/LessonUsageByCategoryReport";
import LessonUsageByLessonReport from "./features/reports/course/usage/LessonUsageByLessonReport";
import CoursePerformanceReport from "./features/reports/course/performance/PerformanceReport";
import CoursePlagiarismReport from "./features/reports/course/plagiarism/PlagiarismReport";
import CourseProgressReport from "./features/reports/course/progress/ProgressReport";
import CoursePromptReport from "./features/reports/course/prompt/PromptReport";
import CoursePromptScoresReport from "./features/reports/course/prompt/ScoresByPrompt";
import AdminReports from "./features/reports/admin/AdminReports";
import UsageSummary from "./features/reports/admin/usage/UsageSummary";
import EssaysAndDrafts from "./features/reports/admin/usage/EssaysAndDrafts";
import DraftUsageOverTime from "./features/reports/admin/usage/DraftUsageOverTime";
import PeerReviewUsage from "./features/reports/admin/usage/PeerReviewUsage";
import LessonUsage from "./features/reports/admin/usage/LessonUsage";
import LessonUsageByCategory from "./features/reports/admin/usage/LessonUsageByCategory";
import HolisticAndTraitScores from "./features/reports/admin/performance/HolisticAndTraitScores";
import HolisticAndTraitScoresByGenre from "./features/reports/admin/performance/HolisticAndTraitByGenreScores";
import GenreAverageScores from "./features/reports/admin/performance/GenreAverageScores";
import UsageAndPerformanceByPrompt from "./features/reports/admin/prompt/UsageAndPerformanceByPrompt";
import PromptUsageAndPerformanceByLevel from "./features/reports/admin/prompt/PromptUsageAndPerformanceByLevel";
import AverageMonthlyScores from "./features/reports/admin/progress/AverageMonthlyScores";
import ScoreReport from "./features/scoreReport/ScoreReport";
import AdminPrompts from "./features/prompts/admin/AdminPrompts";
import AddEditPrompt from "./features/prompts/AddEditPrompt";
import Districts from "./features/districts/Districts";
import SelectDistrict from "./features/districts/SelectDistrict";
import EditDistrict from "./features/districts/EditDistrict";
import SelectSchool from "./features/schools/SelectSchool";
import SchoolList from "./features/schools/SchoolList";
import EditSchool from "./features/schools/EditSchool";
import RosterImport from "./features/rosterImport/RosterImport";
import UsersList from "./features/users/UsersList";
import EditUser from "./features/users/editUserByAdmin/EditUser";
import MyAccount from "./features/account/MyAccount";
import ForgotPassword from "./features/account/ForgotPassword";
import ResetPassword from "./features/account/ResetPassword";
import RegisterProctor from "./features/registration/RegisterProctor";
import Notifications from "./features/notifications/Notifications";
import Impersonation from "./features/impersonation/Impersonation";
import Alerts from "./features/alerts/Alerts";
import AdminAlerts from "./features/alerts/AdminAlerts";
import AdminSecrets from "./features/secrets/AdminSecrets";
import TermsOfService from "./features/termsOfService/TermsOfService";
import NotImplemented from "./features/notImplemented/NotImplemented";
import Demo from "./features/demo/Demo";

export default function App() {
	const { config } = useAppSelector((state) => state.site);
	const dispatch = useAppDispatch();

	let basename = "/spa";
	let virtualDirectory = "";
	const index = window.location.pathname.indexOf(basename);
	if (index > 0) {
		virtualDirectory = window.location.pathname.substring(0, index);
		basename = `${virtualDirectory}${basename}`;
	}

	const origin = window.location.origin;

	useEffect(() => {
		if (!(origin.includes(`localhost:5000`) || origin.includes(`127.0.0.1:5000`))) {
			dispatch(setAspUrl(`${origin}${virtualDirectory}`));
		}
	}, [origin, virtualDirectory]);

	useEffect(() => {
		if (config) {
			document.title = `${config.siteName}: New Experience`;
			let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");

			if (!link) {
				link = document.createElement("link");
				link.rel = "icon";
				link.type = "image/png";
				document.getElementsByTagName("head")[0].appendChild(link);
			}
			if (link) {
				link.href = `favicon-${config.site}.png`;
			}
		}
	}, [config?.siteName]);

	const routes = createRoutesFromElements(
		<Route path="" element={<RouteFromArg />} errorElement={<ErrorPage />}>
			<Route path="" element={<SiteConfig />}>
				<Route path="oauthCallback" element={<OAuthPopup />} />
				<Route element={<Public />}>
					<Route path="/" element={<Login />} />
					<Route path="login" element={<Navigate replace to="/" />} />
					<Route path="logout" element={<Logout />} />
					<Route path="forgotPassword" element={<ForgotPassword />} />
					<Route path="resetPassword" element={<ResetPassword />} />
					<Route path="register" element={<RegisterProctor />} />
					<Route path="status" element={<Status />} />
				</Route>
				<Route element={<Private />}>
					<Route element={<RequireAdmin />}>
						<Route element={<RequireRoles requiredRoles={[UserRole.DistrictAdmin]} />}>
							<Route path="selectDistrict" element={<SelectDistrict />} />
						</Route>
						<Route element={<RequireRoles requiredRoles={[UserRole.SchoolAdmin]} />}>
							<Route path="selectSchool" element={<SelectSchool />} />
						</Route>
						<Route element={<RequireAdminSession />}>
							<Route path="adminHome" element={<Home />} />
							<Route path="prompts">
								<Route index element={<AdminPrompts />} />
								<Route path="prompt" element={<AddEditPrompt />} />
							</Route>
							<Route path="schools">
								<Route
									element={
										<RequireRoles
											requiredRoles={[
												UserRole.Admin,
												UserRole.StateAdmin,
												UserRole.DistrictAdmin,
											]}
										/>
									}>
									<Route index element={<SchoolList />} />
								</Route>
								<Route path="edit" element={<EditSchool />} />
								<Route path="course" element={<EditCourseByAdmin />} />
								<Route path="rosterImport" element={<RosterImport />} />
							</Route>
							<Route
								element={
									<RequireRoles
										requiredRoles={[UserRole.Admin, UserRole.StateAdmin]}
									/>
								}>
								<Route path="districts">
									<Route index element={<Districts />} />
									<Route path="edit" element={<EditDistrict />} />
								</Route>
							</Route>
							<Route path="users">
								<Route index element={<UsersList />} />
								<Route path="edit" element={<EditUser />} />
							</Route>
							<Route element={<RequireRoles requiredRoles={[UserRole.Admin]} />}>
								<Route path="alerts" element={<AdminAlerts />} />
								<Route
									path="features"
									element={
										<NotImplemented title="Features" legacyLink="/Features" />
									}
								/>
								<Route path="demo" element={<Demo />} />
								<Route path="secrets" element={<AdminSecrets />} />
							</Route>
							<Route path="adminReports">
								<Route index element={<AdminReports />} />
								<Route path="usageSummary" element={<UsageSummary />} />
								<Route path="essaysAndDrafts" element={<EssaysAndDrafts />} />
								<Route path="draftUsage" element={<DraftUsageOverTime />} />
								<Route path="reviewsUsage" element={<PeerReviewUsage />} />
								<Route path="lessonUsage" element={<LessonUsage />} />
								<Route
									path="lessonUsageByCategory"
									element={<LessonUsageByCategory />}
								/>
								<Route
									path="holisticAndTraitScores"
									element={<HolisticAndTraitScores />}
								/>
								<Route
									path="holisticAndTraitScoresByGenre"
									element={<HolisticAndTraitScoresByGenre />}
								/>
								<Route path="scoresByGenre" element={<GenreAverageScores />} />
								<Route
									path="usageAndPerformanceByPrompt"
									element={<UsageAndPerformanceByPrompt />}
								/>
								<Route
									path="promptUsageAndPerformanceByLevel"
									element={<PromptUsageAndPerformanceByLevel />}
								/>
								<Route path="averageScores" element={<AverageMonthlyScores />} />
							</Route>
						</Route>
					</Route>
					<Route
						element={
							<RequireRoles requiredRoles={[UserRole.Proctor, UserRole.Writer]} />
						}>
						<Route path="notifications" element={<Notifications />} />
						<Route path="courses" element={<Courses />} />
						<Route element={<RequireCourse />}>
							<Route path="home" element={<Home />} />
							<Route path="writing">
								<Route index element={<Writing />} />
								<Route path="prompt" element={<AddEditPrompt />} />
								<Route path="organizers" element={<SelectOrganizer />} />
								<Route path="organize" element={<Organize />} />
								<Route path="preview" element={<Preview />} />
								<Route path="write" element={<Write />} />
							</Route>
							<Route path="reviews">
								<Route index element={<PeerReviews />} />
								<Route index path=":id" element={<PeerReview />} />
							</Route>
							<Route path="lessons">
								<Route index element={<Lessons />} />
								<Route path=":codeName" element={<Lesson />} />
							</Route>
							<Route path="course">
								<Route index element={<ManageCourse />} />
							</Route>
						</Route>
					</Route>
					<Route element={<RequireCourseOrAdminSession />}>
						<Route path="writing">
							<Route path="viewDrafts" element={<ViewDraftAutosaveHistory />} />
						</Route>
						<Route path="reports">
							<Route index element={<Reports />} />
							<Route path="student">
								<Route index element={<StudentReports />} />
								<Route path="performance" element={<StudentPerformanceReport />} />
								<Route path="progress" element={<StudentProgressReport />} />
								<Route path="prompt" element={<StudentPromptReport />} />
								<Route path="usage" element={<StudentUsageReport />} />
							</Route>
							<Route path="course">
								<Route index element={<CourseReports />} />
								<Route path="performance" element={<CoursePerformanceReport />} />
								<Route path="plagiarism" element={<CoursePlagiarismReport />} />
								<Route path="progress" element={<CourseProgressReport />} />
								<Route path="prompt">
									<Route index element={<CoursePromptReport />} />
									<Route path="scores" element={<CoursePromptScoresReport />} />
								</Route>
								<Route path="usage">
									<Route index element={<CourseUsageReport />} />
									<Route
										path="lessonCategory"
										element={<LessonUsageByCategoryReport />}
									/>
									<Route path="lesson" element={<LessonUsageByLessonReport />} />
								</Route>
							</Route>
						</Route>
						<Route path="scoreReport">
							<Route index element={<ScoreReport />} />
						</Route>
					</Route>
					<Route path="myAccount" element={<MyAccount />} />
					<Route path="myAlerts" element={<Alerts />} />
					<Route path="impersonation" element={<Impersonation />} />
					<Route path="termsOfService" element={<TermsOfService />} />
				</Route>
			</Route>
		</Route>
	);

	const router = createBrowserRouter(routes, {
		basename: basename,
	});

	return <RouterProvider router={router} />;
}
